import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ user, children }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [canCreateMore, setCanCreateMore] = useState(false);
  const [planTier, setPlanTier] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setWorkspaces([]); setActiveId(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/workspaces");
      setWorkspaces(data.workspaces || []);
      setActiveId(data.active_id || null);
      setCanCreateMore(!!data.can_create_more);
      setPlanTier(data.plan_tier || "");
    } catch (err) {
      // Expected during logout / auth transitions — surface at debug level only.
      console.debug("Workspace refresh skipped", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const activeWorkspace = workspaces.find((w) => w.id === activeId) || null;

  const switchTo = async (id) => {
    if (!id || id === activeId) return;
    const { data } = await api.post(`/workspaces/${id}/activate`);
    setActiveId(data.active_id);
    return data;
  };

  const create = async (name) => {
    const { data } = await api.post("/workspaces", { name });
    await refresh();
    return data;
  };

  const rename = async (id, name) => {
    await api.put(`/workspaces/${id}`, { name });
    await refresh();
  };

  const remove = async (id) => {
    await api.delete(`/workspaces/${id}`);
    await refresh();
  };

  return (
    <WorkspaceContext.Provider
      value={{ workspaces, activeId, activeWorkspace, canCreateMore, planTier, loading, refresh, switchTo, create, rename, remove }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used inside WorkspaceProvider");
  return ctx;
}
